/**
 * React Hook for Google Analytics Integration
 * 
 * Provides easy-to-use functions for tracking events and page views
 * with proper SSR support and error handling.
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  initializeGA,
  trackPageView,
  trackEvent,
  trackMetalPriceEvent,
  trackDealerEvent,
  trackBlogEvent,
  trackAuthEvent,
  trackSearchEvent,
  trackConversionEvent,
  trackDealerServiceView,
  trackPerformanceMetrics,
  setUserProperties
} from '@/services/analytics/googleAnalytics';

/**
 * Main Google Analytics hook
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();

  // Initialize Google Analytics on mount
  useEffect(() => {
    initializeGA();
    trackPerformanceMetrics();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    trackPageView(currentPath);
  }, [location]);

  // Memoized tracking functions
  const trackCustomEvent = useCallback((
    eventName: string,
    parameters?: Record<string, any>
  ) => {
    trackEvent(eventName, parameters);
  }, []);

  const trackMetalPrice = useCallback((
    action: 'view_price' | 'price_alert_set' | 'price_chart_view',
    metalType: 'gold' | 'silver',
    additionalData?: Record<string, any>
  ) => {
    trackMetalPriceEvent(action, metalType, additionalData);
  }, []);

  const trackDealer = useCallback((
    action: 'view_dealer' | 'contact_dealer' | 'favorite_dealer' | 'rate_dealer',
    dealerId: string,
    dealerLocation?: string,
    additionalData?: Record<string, any>
  ) => {
    trackDealerEvent(action, dealerId, dealerLocation, additionalData);
  }, []);

  const trackBlog = useCallback((
    action: 'view_post' | 'share_post' | 'search_blog',
    postId?: string,
    category?: string,
    additionalData?: Record<string, any>
  ) => {
    trackBlogEvent(action, postId, category, additionalData);
  }, []);

  const trackAuth = useCallback((
    action: 'login' | 'register' | 'logout' | 'password_reset',
    method?: string,
    additionalData?: Record<string, any>
  ) => {
    trackAuthEvent(action, method, additionalData);
  }, []);

  const trackSearch = useCallback((
    searchTerm: string,
    searchType: 'dealers' | 'blog' | 'general',
    resultsCount?: number
  ) => {
    trackSearchEvent(searchTerm, searchType, resultsCount);
  }, []);

  const trackConversion = useCallback((
    conversionType: 'contact_form' | 'dealer_activation_request' | 'newsletter_signup',
    value?: number,
    additionalData?: Record<string, any>
  ) => {
    trackConversionEvent(conversionType, value, additionalData);
  }, []);

  const trackDealerService = useCallback((
    dealerId: string,
    dealerName: string,
    location: string,
    serviceType?: string
  ) => {
    trackDealerServiceView(dealerId, dealerName, location, serviceType);
  }, []);

  const setUserProps = useCallback((properties: Record<string, any>) => {
    setUserProperties(properties);
  }, []);

  return {
    trackCustomEvent,
    trackMetalPrice,
    trackDealer,
    trackBlog,
    trackAuth,
    trackSearch,
    trackConversion,
    trackDealerService,
    setUserProps
  };
};

/**
 * Hook specifically for metal price tracking
 */
export const useMetalPriceTracking = () => {
  const { trackMetalPrice } = useGoogleAnalytics();

  const trackPriceView = useCallback((metalType: 'gold' | 'silver', currentPrice?: number) => {
    trackMetalPrice('view_price', metalType, { current_price: currentPrice });
  }, [trackMetalPrice]);

  const trackPriceAlert = useCallback((
    metalType: 'gold' | 'silver',
    alertType: 'above' | 'below',
    targetPrice: number
  ) => {
    trackMetalPrice('price_alert_set', metalType, {
      alert_type: alertType,
      target_price: targetPrice
    });
  }, [trackMetalPrice]);

  const trackChartView = useCallback((
    metalType: 'gold' | 'silver',
    timeframe: string,
    chartType?: string
  ) => {
    trackMetalPrice('price_chart_view', metalType, {
      timeframe,
      chart_type: chartType
    });
  }, [trackMetalPrice]);

  return {
    trackPriceView,
    trackPriceAlert,
    trackChartView
  };
};

/**
 * Hook specifically for dealer tracking
 */
export const useDealerTracking = () => {
  const { trackDealer, trackDealerService } = useGoogleAnalytics();

  const trackDealerView = useCallback((
    dealerId: string,
    dealerName: string,
    dealerLocation?: string
  ) => {
    trackDealer('view_dealer', dealerId, dealerLocation, { dealer_name: dealerName });
    trackDealerService(dealerId, dealerName, dealerLocation || '');
  }, [trackDealer, trackDealerService]);

  const trackDealerContact = useCallback((
    dealerId: string,
    contactMethod: 'phone' | 'email' | 'form',
    dealerLocation?: string
  ) => {
    trackDealer('contact_dealer', dealerId, dealerLocation, {
      contact_method: contactMethod
    });
  }, [trackDealer]);

  const trackDealerFavorite = useCallback((
    dealerId: string,
    action: 'add' | 'remove',
    dealerLocation?: string
  ) => {
    trackDealer('favorite_dealer', dealerId, dealerLocation, {
      favorite_action: action
    });
  }, [trackDealer]);

  const trackDealerRating = useCallback((
    dealerId: string,
    rating: number,
    dealerLocation?: string
  ) => {
    trackDealer('rate_dealer', dealerId, dealerLocation, {
      rating_value: rating
    });
  }, [trackDealer]);

  return {
    trackDealerView,
    trackDealerContact,
    trackDealerFavorite,
    trackDealerRating
  };
};

/**
 * Hook specifically for blog tracking
 */
export const useBlogTracking = () => {
  const { trackBlog } = useGoogleAnalytics();

  const trackBlogPostView = useCallback((
    postId: string,
    postTitle: string,
    category?: string,
    readingTime?: number
  ) => {
    trackBlog('view_post', postId, category, {
      post_title: postTitle,
      reading_time: readingTime
    });
  }, [trackBlog]);

  const trackBlogPostShare = useCallback((
    postId: string,
    shareMethod: 'facebook' | 'twitter' | 'linkedin' | 'whatsapp' | 'copy_link',
    category?: string
  ) => {
    trackBlog('share_post', postId, category, {
      share_method: shareMethod
    });
  }, [trackBlog]);

  const trackBlogSearch = useCallback((
    searchTerm: string,
    resultsCount: number,
    selectedCategory?: string
  ) => {
    trackBlog('search_blog', undefined, selectedCategory, {
      search_term: searchTerm,
      results_count: resultsCount
    });
  }, [trackBlog]);

  return {
    trackBlogPostView,
    trackBlogPostShare,
    trackBlogSearch
  };
};

/**
 * Hook specifically for authentication tracking
 */
export const useAuthTracking = () => {
  const { trackAuth, setUserProps } = useGoogleAnalytics();

  const trackLogin = useCallback((
    method: 'email' | 'google' | 'facebook' | 'biometric',
    success: boolean,
    userId?: string
  ) => {
    trackAuth('login', method, { success, user_id: userId });
    
    if (success && userId) {
      setUserProps({
        user_id: userId,
        login_method: method
      });
    }
  }, [trackAuth, setUserProps]);

  const trackRegister = useCallback((
    method: 'email' | 'google' | 'facebook',
    success: boolean,
    userId?: string
  ) => {
    trackAuth('register', method, { success, user_id: userId });
    
    if (success && userId) {
      setUserProps({
        user_id: userId,
        registration_method: method
      });
    }
  }, [trackAuth, setUserProps]);

  const trackLogout = useCallback(() => {
    trackAuth('logout');
    setUserProps({ user_id: null });
  }, [trackAuth, setUserProps]);

  const trackPasswordReset = useCallback((
    method: 'email' | 'sms',
    success: boolean
  ) => {
    trackAuth('password_reset', method, { success });
  }, [trackAuth]);

  return {
    trackLogin,
    trackRegister,
    trackLogout,
    trackPasswordReset
  };
};

/**
 * Hook specifically for conversion tracking
 */
export const useConversionTracking = () => {
  const { trackConversion } = useGoogleAnalytics();

  const trackContactForm = useCallback((
    formType: 'general' | 'dealer_contact' | 'support',
    success: boolean,
    leadValue?: number
  ) => {
    trackConversion('contact_form', leadValue, {
      form_type: formType,
      success
    });
  }, [trackConversion]);

  const trackDealerActivation = useCallback((
    dealerId: string,
    activationType: 'new' | 'reactivation',
    estimatedValue?: number
  ) => {
    trackConversion('dealer_activation_request', estimatedValue, {
      dealer_id: dealerId,
      activation_type: activationType
    });
  }, [trackConversion]);

  const trackNewsletterSignup = useCallback((
    source: 'homepage' | 'blog' | 'popup' | 'footer',
    success: boolean
  ) => {
    trackConversion('newsletter_signup', undefined, {
      source,
      success
    });
  }, [trackConversion]);

  return {
    trackContactForm,
    trackDealerActivation,
    trackNewsletterSignup
  };
};
